import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SquareEnvironment, UpdateSquareSettingsInput } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useConfig } from "../config/ConfigProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";

/** Yes/no chip for whether a credential is currently stored. */
function SetBadge({ set }: { set: boolean }) {
  return set ? (
    <span className="badge badge--success">Set</span>
  ) : (
    <span className="badge badge--muted">Not set</span>
  );
}

const ENV_LABEL: Record<SquareEnvironment, string> = {
  production: "Production",
  sandbox: "Sandbox",
};

export function SquareIntegration() {
  const api = useApi();
  const config = useConfig();

  const { data, loading, error, reload, setData } = useAsync(
    () => api.getSquareSettings(),
    [api],
  );

  // The webhook URL to paste into Square's Developer Dashboard subscription.
  const webhookUrl = `${config.apiBaseUrl.replace(/\/+$/, "")}/integrations/square/webhook`;

  const [accessToken, setAccessToken] = useState("");
  const [signatureKey, setSignatureKey] = useState("");
  const [locationId, setLocationId] = useState("");
  const [notificationUrl, setNotificationUrl] = useState("");
  // Which environment's credentials this form is editing (not the active one —
  // that is chosen by the site-wide dev-mode toggle on the Integrations hub).
  const [environment, setEnvironment] = useState<SquareEnvironment>("production");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | undefined>(undefined);
  const [syncStarted, setSyncStarted] = useState(false);

  // Prefill the non-secret fields from the loaded status (secrets stay blank).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !data) return;
    setNotificationUrl(data.notificationUrl ?? webhookUrl);
    setLocationId(data.production.locationId ?? "");
    setHydrated(true);
  }, [data, hydrated, webhookUrl]);

  // Switching which environment we edit re-prefills that env's location and
  // clears the write-only secret inputs so they can't leak into the other set.
  const changeEnvironment = (next: SquareEnvironment) => {
    setEnvironment(next);
    setLocationId((next === "production" ? data?.production : data?.sandbox)?.locationId ?? "");
    setAccessToken("");
    setSignatureKey("");
    setSaved(false);
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context); ignore silently.
    }
  };

  const syncAll = async () => {
    setSyncError(undefined);
    setSyncStarted(false);
    setSyncing(true);
    try {
      await api.syncAllToSquare();
      setSyncStarted(true);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : "Could not start the sync.");
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    setSaveError(undefined);
    setSaved(false);

    // Send only what the admin provided; secrets are write-only and skipped when blank.
    const input: UpdateSquareSettingsInput = { environment };
    if (accessToken.trim()) input.accessToken = accessToken.trim();
    if (signatureKey.trim()) input.webhookSignatureKey = signatureKey.trim();
    if (locationId.trim()) input.locationId = locationId.trim();
    if (notificationUrl.trim()) input.notificationUrl = notificationUrl.trim();

    setSaving(true);
    try {
      const next = await api.updateSquareSettings(input);
      setData(() => next);
      setAccessToken("");
      setSignatureKey("");
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner label="Loading…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  const status = data;
  const anyConfigured = Boolean(status?.production.configured || status?.sandbox.configured);
  const envStatus = environment === "production" ? status?.production : status?.sandbox;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Square integration</h1>
          <p className="page__subtitle page__subtitle--badges">
            {anyConfigured ? (
              <span className="badge badge--success">Connected</span>
            ) : (
              <span className="badge badge--muted">Not configured</span>
            )}
            <span className="muted">
              Our inventory is the master; Square mirrors our counts and sales decrement our stock.
            </span>
          </p>
        </div>
      </div>

      <p className="form-hint muted">
        The active environment is set site-wide by the{" "}
        <Link to="/settings/integrations">development-mode toggle</Link>. Store credentials for both
        environments here so flipping the switch never requires re-entering them.
      </p>

      {saveError ? <ErrorBanner title="Save failed" message={saveError} /> : null}

      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Status</h2>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => void syncAll()}
            disabled={syncing || !anyConfigured}
            title={
              anyConfigured
                ? "Push every product's current count to Square (active environment)"
                : "Configure Square before syncing"
            }
          >
            {syncing ? "Starting…" : "Sync all to Square"}
          </button>
        </div>
        {syncError ? <ErrorBanner title="Sync failed" message={syncError} /> : null}
        {syncStarted ? (
          <p className="form-hint muted">
            Sync started — counts are pushed to the active environment in the background. Products
            without a matching Square SKU are skipped.
          </p>
        ) : null}
        <dl className="detail-grid">
          {(["production", "sandbox"] as const).map((env) => {
            const s = status?.[env];
            return (
              <div key={env}>
                <dt className="muted">
                  {ENV_LABEL[env]} {s?.configured ? <SetBadge set /> : <SetBadge set={false} />}
                </dt>
                <dd>
                  <div>
                    Access token: <SetBadge set={Boolean(s?.hasAccessToken)} />
                  </div>
                  <div>
                    Signature key: <SetBadge set={Boolean(s?.hasSignatureKey)} />
                  </div>
                  <div className="muted">
                    Location: {s?.locationId ? s.locationId : "Auto (first active)"}
                  </div>
                </dd>
              </div>
            );
          })}
          <div>
            <dt className="muted">Last updated</dt>
            <dd>
              {status?.updatedAt ? (
                <>
                  {new Date(status.updatedAt).toLocaleString()}
                  {status.updatedBy ? ` by ${status.updatedBy}` : ""}
                </>
              ) : (
                <span className="muted">Never</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Webhook URL</h2>
        </div>
        <p className="muted">
          In the Square Developer Dashboard, add a webhook subscription to this URL and subscribe to
          the <strong>payment.updated</strong> event. Copy the subscription&rsquo;s signature key
          back into the field below so we can verify incoming events.
        </p>
        <div className="form-field">
          <div className="page__subtitle--badges">
            <textarea aria-label="Webhook URL" className="input webhook-url" rows={3} value={webhookUrl} readOnly onFocus={(e) => e.target.select()} />
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => void copyWebhookUrl()}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </section>

      <form
        className="entry-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Credentials</h2>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="square-environment">
              These credentials are for
            </label>
            <select
              id="square-environment"
              className="input"
              value={environment}
              onChange={(e) => changeEnvironment(e.target.value as SquareEnvironment)}
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
            <span className="form-hint muted">
              Which environment this set belongs to — not which is active. The active one is chosen by
              the site-wide dev-mode toggle.
            </span>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="square-access-token">
              Access token
            </label>
            <input
              id="square-access-token"
              className="input"
              type="password"
              autoComplete="off"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={envStatus?.hasAccessToken ? "•••••••• (leave blank to keep)" : "Paste the Square access token"}
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="square-signature-key">
              Webhook signature key
            </label>
            <input
              id="square-signature-key"
              className="input"
              type="password"
              autoComplete="off"
              value={signatureKey}
              onChange={(e) => setSignatureKey(e.target.value)}
              placeholder={envStatus?.hasSignatureKey ? "•••••••• (leave blank to keep)" : "Paste the webhook signature key"}
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="square-location-id">
              Location ID <span className="muted">(optional — defaults to the first active location)</span>
            </label>
            <input
              id="square-location-id"
              className="input"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            />
          </div>
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Notification URL</h2>
          </div>
          <div className="form-field">
            <label className="sr-only" htmlFor="square-notification-url">Notification URL</label>
            <input
              id="square-notification-url"
              className="input"
              value={notificationUrl}
              onChange={(e) => setNotificationUrl(e.target.value)}
              placeholder={webhookUrl}
            />
            <span className="form-hint muted">
              Shared across both environments. Must exactly match the subscription URL in Square —
              signatures are computed over it.
            </span>
          </div>
        </section>

        <div className="form-actions form-actions--split">
          <div className="form-actions__group">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Saving…" : `Save ${ENV_LABEL[environment]} settings`}
            </button>
            {saved ? <span className="muted">Saved.</span> : null}
          </div>
        </div>
      </form>
    </div>
  );
}
